/**
 * GlaWel Liefertermin Import — Frontend
 *
 * Architektur:
 * - useJobStatus aus Core → Tab-Wechsel ohne Abbruch (Job laeuft im Backend)
 * - Live-Liste der bearbeiteten Auftraege mit gruen/rot waehrend des Laufs
 * - Vorschau zeigt nur ER-Auftraege die noch nicht gebucht wurden + alle DR-Auftraege
 * - CI-Farben: #E9FF57, #1A1A1A, #DAD9D9
 */

import { useState, useEffect, useMemo } from 'react';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import { Upload, Eye, AlertCircle, CheckCircle, XCircle, Download, FileSpreadsheet, RefreshCw, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import KundeninfoPanel from '../../../liefertermin-shared/frontend/src/KundeninfoPanel';
import LieferverzugPanel from '../../../liefertermin-shared/frontend/src/lieferverzug/LieferverzugPanel';
import { ReiterLeiste, type LieferterminReiter } from '../../../liefertermin-shared/frontend/src/lieferverzug/ReiterLeiste';

// ─── Typen ────────────────────────────────────────────────────────────────────

type ActionType = 'ER' | 'DR';

interface OrderProcessingResult {
  orderId: string;
  plentyOrderId?: number;
  success: boolean;
  noteCreated: boolean;
  noteVerified: boolean;
  invoiceCreated: boolean;
  invoiceVerified: boolean;
  finalStatus?: number;
  error?: string;
  timestamp: string;
  actionType: ActionType;
  plentyResponse?: string;
  deliveryTime?: string;
  statusBefore?: number;
  noteBefore?: string;
  noteAfter?: string;
  skipped?: boolean;
  skipReason?: string;
  openPositionsList?: string[];
  isNachlieferung?: boolean;
  nachlieferungReason?: string;
  statusSet?: boolean;
  statusSetTo?: number;
  statusReason?: string;
  // Terminvergleich Auftragsbestaetigung ↔ geplante Liefer-KW
  abNummer?: string | null;
  abDatum?: string | null;
  abKwLabel?: string | null;
  abDifferenzTage?: number | null;
  abDifferenzWochen?: number | null;
  abWarnung?: boolean;
  abHinweis?: string;
}

interface ImportResult {
  totalRows: number;
  processedOrders: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  cancelledCount?: number;
  errors: Array<{ orderId: string; error: string }>;
  sheetName: string;
  detailedResults: OrderProcessingResult[];
  statusMode?: 'note-only' | 'note-and-status';
  nachlieferungCandidates?: number;
  statusSetCount?: number;
  statusCapHit?: boolean;
  isComplete?: boolean;
  abWarnCount?: number;
  abOhneAbCount?: number;
}

interface PreviewRow {
  rowNumber: number;
  action: ActionType | '';
  orderId: string;
  deliveryTime: string;
  openPositions?: string[];
  alreadyBooked: boolean;
  willProcess: boolean;
  isNachlieferung?: boolean;
  nachlieferungReason?: string;
  mailPreview?: string;
  abNummer?: string | null;
  abDatum?: string | null;
  abKwLabel?: string | null;
  abDifferenzTage?: number | null;
  abDifferenzWochen?: number | null;
  abWarnung?: boolean;
  abHinweis?: string;
}

interface PreviewData {
  totalRows: number;
  previewRows: PreviewRow[];
  outgoingNewCount: number;
  outgoingSkippedCount: number;
  noteCount: number;
  nachlieferungCount?: number;
  sheetName: string;
  fehlendeVorgaenge?: Array<{ auftragsnr: string; bestelltAm: string | null; istNeu: boolean }>;
}

/**
 * Zelle "AB / Differenz": Kalenderwoche der Auftragsbestaetigung, ihr Datum und
 * die Spanne bis zum geplanten Liefertermin. Ueberschreitet die Spanne die
 * konfigurierte Schwelle, wird die Zeile rot markiert.
 *
 * Fehlt die AB, steht das ausdruecklich da — "keine AB gefunden" darf nie wie
 * "alles in Ordnung" aussehen.
 */
function AbVergleichZelle({ row }: { row: { abKwLabel?: string | null; abDatum?: string | null; abDifferenzWochen?: number | null; abWarnung?: boolean; abHinweis?: string } }) {
  const datumDe = row.abDatum
    ? `${row.abDatum.slice(8, 10)}.${row.abDatum.slice(5, 7)}.${row.abDatum.slice(0, 4)}`
    : null;

  if (!row.abDatum) {
    return (
      <span style={{ fontSize: '0.72rem', color: '#9e9e9e', fontStyle: 'italic' }}>
        {row.abHinweis || 'keine AB gefunden'}
      </span>
    );
  }

  return (
    <div title={row.abHinweis}>
      <div style={{ fontWeight: 600, fontSize: '0.78rem' }}>{row.abKwLabel}</div>
      <div style={{ fontSize: '0.7rem', color: '#666' }}>{datumDe}</div>
      {row.abDifferenzWochen !== null && row.abDifferenzWochen !== undefined && (
        <div style={{ marginTop: '0.2rem' }}>
          <span style={{
            padding: '0.1rem 0.45rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 700,
            backgroundColor: row.abWarnung ? '#c62828' : '#eceff1',
            color: row.abWarnung ? '#fff' : '#37474f',
          }}>
            {row.abWarnung ? '⚠ ' : ''}{String(row.abDifferenzWochen).replace('.', ',')} Wochen
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Komponente ───────────────────────────────────────────────────────────────

export default function GlaWelApp() {
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [testMode, setTestMode] = useState(false);
  const [setStatusMode, setSetStatusMode] = useState(false); // false = nur Notiz, true = Notiz + Status 5.67
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set()); // ausgewaehlte Excel-Zeilen (rowNumber)
  const [showInfo, setShowInfo] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Deep-Link aus der Bericht-Mail: ?tab=verzug oeffnet direkt den Lieferverzug-Reiter.
  const [reiter, setReiter] = useState<LieferterminReiter>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'verzug' ? 'verzug' : 'import');

  const { status: jobStatus, isRunning, progress, job } = useJobStatus(jobId);

  useEffect(() => {
    loadAvailableFiles();
  }, []);

  useEffect(() => {
    if (selectedFile) {
      loadSheets();
      setPreviewData(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (selectedFile && selectedSheet) {
      loadPreview();
    }
  }, [selectedFile, selectedSheet]);

  // Live-Update: detailedResults aus dem Job lesen, sobald sie sich aendern
  useEffect(() => {
    const live = (job as any)?.resultSummary as ImportResult | undefined;
    if (live && Array.isArray(live.detailedResults)) {
      setImportResult(live);
    }
  }, [job]);

  // Wenn Job fertig: Preview neu laden, damit "schon gebucht"-Liste up-to-date ist
  useEffect(() => {
    if (jobStatus === 'completed' && selectedFile && selectedSheet) {
      loadPreview();
    }
  }, [jobStatus]);

  // ─── API-Calls ───────────────────────────────────────────────────────────

  async function loadAvailableFiles() {
    try {
      setIsLoadingFiles(true);
      setErrorMsg(null);
      const resp = await (api as any).get<{ success: boolean; files: string[] }>(
        '/programmes/glawel-liefertermin/files',
      );
      const files: string[] = resp.data?.files || [];
      setAvailableFiles(files);
      if (files.length > 0) {
        // Bevorzugt Datei mit "GlaWel" im Namen
        const preferred = files.find((f) => /glawel/i.test(f)) || files[0];
        setSelectedFile(preferred);
      }
    } catch (error: any) {
      setErrorMsg('Fehler beim Laden der Dateien: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setIsLoadingFiles(false);
    }
  }

  async function loadSheets() {
    if (!selectedFile) return;
    try {
      const resp = await (api as any).post<{ success: boolean; sheetNames: string[] }>(
        '/programmes/glawel-liefertermin/sheet-names',
        { filename: selectedFile },
      );
      if (resp.data?.success && resp.data.sheetNames) {
        setAvailableSheets(resp.data.sheetNames);
        setSelectedSheet(resp.data.sheetNames[0]);
      }
    } catch {
      setAvailableSheets([]);
      setSelectedSheet('');
    }
  }

  async function loadPreview() {
    if (!selectedFile || !selectedSheet) return;
    try {
      setIsLoadingPreview(true);
      const resp = await (api as any).post<{ success: boolean; preview: PreviewData }>(
        '/programmes/glawel-liefertermin/preview',
        { filename: selectedFile, sheetName: selectedSheet },
      );
      if (resp.data?.success) {
        setPreviewData(resp.data.preview);
      }
    } catch {
      setPreviewData(null);
    } finally {
      setIsLoadingPreview(false);
    }
  }

  async function handleImport() {
    if (!selectedFile || !selectedSheet) {
      setErrorMsg('Bitte Datei und Excel-Reiter auswaehlen');
      return;
    }
    setErrorMsg(null);
    setImportResult(null);
    try {
      const resp = await (api as any).post<{ success: boolean; data: { jobId: string } }>(
        '/programmes/glawel-liefertermin/execute',
        {
          filename: selectedFile,
          sheetName: selectedSheet,
          testMode,
          setStatus: setStatusMode,
          // Auswahl: nur die ausgewaehlten Excel-Zeilen. Wenn alle ausgewaehlt sind → null (= alle, bisheriges Verhalten).
          selectedRowNumbers: allSelected ? null : Array.from(selectedRows),
        },
      );
      if (resp.data?.success && resp.data.data?.jobId) {
        setJobId(resp.data.data.jobId);
      } else {
        setErrorMsg('Fehler beim Starten des Imports');
      }
    } catch (error: any) {
      setErrorMsg('Fehler beim Starten des Imports: ' + (error.response?.data?.error?.message || error.message));
    }
  }


  /**
   * Excel-Export direkt aus der VORSCHAU — ohne Import-Lauf.
   *
   * Zweck: die Arbeitsliste zum Nachfassen beim Lieferanten soll ohne jeden
   * Schreibzugriff auf Plenty entstehen. Der Import wuerde Notizen schreiben und
   * ggf. Kundenmails ausloesen; wer nur auswerten will, braucht das nicht.
   */
  function handleExportPreviewWarnungen() {
    if (!previewData) return;
    const zeilen = previewData.previewRows.filter((r) => r.abWarnung);
    if (zeilen.length === 0) return;

    const wb = XLSX.utils.book_new();
    const dateStr = new Date().toISOString().slice(0, 10);

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ['GlaWel Liefertermin — Auftraege mit langer Wartezeit'],
        ['Export-Datum', new Date().toLocaleString('de-DE')],
        ['Quelle', 'Vorschau (kein Import-Lauf, keine Aenderung in Plenty)'],
        ['Verwendeter Reiter', previewData.sheetName || '-'],
        [''],
        ['Kriterium', 'Mehr als 6 Wochen zwischen Auftragsbestaetigung und Montag der geplanten Liefer-KW'],
        [''],
        ['Statistik', 'Anzahl'],
        ['Zeilen in der Vorschau', previewData.previewRows.length],
        ['davon ueber der Schwelle', zeilen.length],
        ['ohne Auftragsbestaetigung', previewData.previewRows.filter((r) => r.action === 'DR' && !r.abDatum).length],
      ]),
      'Zusammenfassung',
    );

    const daten: any[][] = [[
      'Excel-Zeile', 'Aktion', 'Auftragsnr.', 'AB-Nummer', 'AB-Datum', 'KW der AB',
      'Geplante Liefer-KW', 'Differenz (Tage)', 'Differenz (Wochen)', 'Hinweis', 'Offene Positionen',
    ]];
    for (const r of zeilen) {
      daten.push([
        r.rowNumber,
        r.action,
        r.orderId,
        r.abNummer || '-',
        r.abDatum || '-',
        r.abKwLabel || '-',
        r.deliveryTime || '-',
        r.abDifferenzTage ?? '-',
        r.abDifferenzWochen ?? '-',
        r.abHinweis || '-',
        (r.openPositions || []).join(' + ') || '-',
      ]);
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(daten), 'Warnungen');
    XLSX.writeFile(wb, `GlaWel_Wartezeit_ueber_6_Wochen_${dateStr}.xlsx`);
  }

  /**
   * Excel-Export der Ergebnisse.
   * `nurWarnungen` exportiert ausschliesslich Zeilen, deren Spanne zwischen
   * Auftragsbestaetigung und geplantem Liefertermin die Schwelle reisst — die
   * Arbeitsliste zum Nachfassen beim Lieferanten.
   */
  function handleExportResults(nurWarnungen = false) {
    if (!importResult) return;
    const alle = importResult.detailedResults || [];
    const zeilen = nurWarnungen ? alle.filter((r) => r.abWarnung) : alle;
    const wb = XLSX.utils.book_new();

    const summaryData = [
      ['GlaWel Liefertermin Import — Ergebnisse'],
      ['Export-Datum', new Date().toLocaleString('de-DE')],
      ['Verwendeter Reiter', importResult.sheetName || '-'],
      [''],
      ['Statistik', 'Anzahl'],
      ['Gesamte Zeilen', importResult.totalRows],
      ['Verarbeitete Auftraege', importResult.processedOrders],
      ['Erfolgreich', importResult.successCount],
      ['Fehler', importResult.errorCount],
      ['Bereits gebucht (uebersprungen)', importResult.skippedCount ?? 0],
      ['Storniert (uebersprungen, Status > 7)', importResult.cancelledCount ?? 0],
      ['Ueber 6 Wochen zwischen AB und Liefertermin', importResult.abWarnCount ?? 0],
      ['Ohne gefundene Auftragsbestaetigung', importResult.abOhneAbCount ?? 0],
      [''],
      ['Export-Umfang', nurWarnungen ? 'nur Warnungen' : 'alle Zeilen'],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Zusammenfassung');

    const detailsData: any[][] = [
      [
        'Status', 'Auftragsnr.', 'PlentyMarkets ID', 'Aktion', 'KW',
        'Notiz erstellt', 'Notiz verifiziert', 'Warenausgang gebucht', 'Warenausgang verifiziert',
        'Status vorher', 'Endstatus', 'Notiz vorher', 'Notiz nachher',
        'Offene Positionen', 'Nachlieferung (5.67)', 'Status 5.67 gesetzt',
        'AB-Nummer', 'AB-Datum', 'KW der AB', 'Differenz (Tage)', 'Differenz (Wochen)', 'Warnung > 6 Wochen', 'AB-Hinweis',
        'Plenty Rueckmeldung', 'Zeitstempel', 'Fehler', 'Skip-Grund',
      ],
    ];
    for (const r of zeilen) {
      detailsData.push([
        r.skipped ? 'Storniert (uebersprungen)' : (r.success ? 'Erfolgreich' : 'Fehler'),
        r.orderId,
        r.plentyOrderId || '-',
        r.actionType,
        r.deliveryTime || '-',
        r.noteCreated ? 'Ja' : 'Nein',
        r.noteVerified ? 'Ja' : 'Nein',
        r.invoiceCreated ? 'Ja' : 'Nein',
        r.invoiceVerified ? 'Ja' : 'Nein',
        r.statusBefore ?? '-',
        r.finalStatus ?? '-',
        r.noteBefore ?? '-',
        r.noteAfter ?? r.deliveryTime ?? '-',
        Array.isArray(r.openPositionsList) ? r.openPositionsList.join(' + ') : '-',
        r.isNachlieferung ? 'Ja' : 'Nein',
        r.statusSet ? `Ja (${r.statusSetTo ?? '5.67'})` : (r.statusReason || '-'),
        r.abNummer || '-',
        r.abDatum || '-',
        r.abKwLabel || '-',
        r.abDifferenzTage ?? '-',
        r.abDifferenzWochen ?? '-',
        r.abWarnung ? 'JA' : 'Nein',
        r.abHinweis || '-',
        r.plentyResponse || '-',
        r.timestamp || '-',
        r.error || '-',
        r.skipReason || '-',
      ]);
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(detailsData), 'Details');

    if (importResult.errors?.length > 0) {
      const errorsData: any[][] = [['Auftragsnr.', 'Fehler']];
      for (const e of importResult.errors) errorsData.push([e.orderId, e.error]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(errorsData), 'Fehler');
    }

    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
    XLSX.writeFile(wb, `GlaWel_Import_${nurWarnungen ? 'Warnungen' : 'Ergebnis'}_${dateStr}.xlsx`);
  }

  // ─── Hilfsvariablen ──────────────────────────────────────────────────────

  const isProcessing = isRunning;
  const showSheetPicker = availableSheets.length > 1;

  const erRows = useMemo(
    () => previewData?.previewRows.filter((r) => r.action === 'ER') || [],
    [previewData],
  );
  const drRows = useMemo(
    () => previewData?.previewRows.filter((r) => r.action === 'DR') || [],
    [previewData],
  );

  // Auswahl: bei jeder neuen Vorschau standardmaessig ALLE sichtbaren Zeilen auswaehlen.
  useEffect(() => {
    setSelectedRows(new Set((previewData?.previewRows || []).map((r) => r.rowNumber)));
  }, [previewData]);

  const allVisibleRowNumbers = useMemo(
    () => (previewData?.previewRows || []).map((r) => r.rowNumber),
    [previewData],
  );
  const allSelected = allVisibleRowNumbers.length > 0 && allVisibleRowNumbers.every((n) => selectedRows.has(n));
  const toggleRow = (n: number) =>
    setSelectedRows((prev) => {
      const s = new Set(prev);
      if (s.has(n)) s.delete(n); else s.add(n);
      return s;
    });
  const toggleAll = () =>
    setSelectedRows(allSelected ? new Set() : new Set(allVisibleRowNumbers));

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, sans-serif', color: '#1A1A1A' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#1A1A1A' }}>GlaWel Liefertermin Import</h1>
        <p style={{ color: '#555', marginTop: '0.25rem' }}>
          Warenausgaenge buchen (ER) und Lieferterminnotizen schreiben (DR) — fortlaufende Liste mit Doppel-Buchung-Schutz
        </p>
      </div>

      <ReiterLeiste aktiv={reiter} aufWechsel={setReiter} />

      {reiter === 'verzug' && <LieferverzugPanel programmeId="glawel-liefertermin" />}

      {reiter === 'import' && (
      <>
      <KundeninfoPanel programmeId="glawel-liefertermin" />

      {/* Fehler */}
      {errorMsg && (
        <div style={{
          marginBottom: '1rem', padding: '0.75rem 1rem',
          backgroundColor: '#fee', border: '1px solid #f99', borderRadius: '6px',
          color: '#c00', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>×</button>
        </div>
      )}

      {/* Info */}
      {showInfo && (
        <div style={{
          marginBottom: '1.5rem', padding: '1rem',
          backgroundColor: '#e8f4fd', border: '1px solid #b3d9f7', borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <strong>So funktioniert der Import</strong>
            <button onClick={() => setShowInfo(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
          <div style={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
            <p style={{ margin: '0.25rem 0' }}><strong>Datenquelle:</strong> Google Drive Ordner (GlaWel Liefertermin Liste)</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Spalte A:</strong> Aktion — "ER" = Warenausgang buchen, "DR" = Liefertermin-Notiz schreiben</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Spalte E:</strong> Auftragsnummer (6-stellig, Suffix-Text wird ignoriert)</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Spalte F:</strong> Liefertermin als Phrase "KW16" — die KW wird strikt extrahiert</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Spalte "Offene Positionen":</strong> wird bei DR-Auftraegen in die Notiz uebernommen — KW oben, darunter die offenen Positionen untereinander</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Doppel-Buchung-Schutz:</strong> Erfolgreich gebuchte ER-Auftraege werden in der DB gemerkt und beim naechsten Lauf uebersprungen</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Anzeige:</strong> Vorschau zeigt nur Auftraege die in diesem Lauf etwas tun (neue ER + alle DR)</p>
          </div>
        </div>
      )}

      {/* Dateiauswahl */}
      <div style={{ background: '#fff', border: '1px solid #DAD9D9', borderRadius: '8px', marginBottom: '1rem' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.75rem 1rem', borderBottom: '1px solid #DAD9D9'
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Dateiauswahl und Import</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={loadPreview}
              disabled={isProcessing || isLoadingPreview || !selectedFile}
              title="Vorschau neu laden"
              style={{
                padding: '0.25rem 0.75rem', border: '1px solid #DAD9D9', borderRadius: '4px',
                background: '#fff', cursor: isProcessing ? 'not-allowed' : 'pointer', fontSize: '0.875rem',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}
            >
              <RefreshCw size={14} />
              Aktualisieren
            </button>
            <button
              onClick={() => setShowInfo(!showInfo)}
              style={{
                padding: '0.25rem 0.75rem', border: '1px solid #DAD9D9', borderRadius: '4px',
                background: '#fff', cursor: 'pointer', fontSize: '0.875rem',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}
            >
              <Info size={14} />
              Info
            </button>
          </div>
        </div>

        <div style={{ padding: '1rem' }}>
          {isLoadingFiles ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Lade verfuegbare Dateien...</div>
          ) : availableFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Keine Excel-Dateien im Google Drive Ordner gefunden.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                  Excel-Datei auswaehlen:
                </label>
                <select
                  value={selectedFile}
                  onChange={(e) => setSelectedFile(e.target.value)}
                  disabled={isProcessing}
                  style={{
                    width: '100%', padding: '0.5rem', border: '1px solid #DAD9D9',
                    borderRadius: '4px', fontSize: '0.875rem', backgroundColor: '#fff',
                  }}
                >
                  {availableFiles.map((file) => (
                    <option key={file} value={file}>{file}</option>
                  ))}
                </select>
              </div>

              {showSheetPicker && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Excel-Reiter auswaehlen:
                  </label>
                  <select
                    value={selectedSheet}
                    onChange={(e) => setSelectedSheet(e.target.value)}
                    disabled={isProcessing}
                    style={{
                      width: '100%', padding: '0.5rem', border: '1px solid #DAD9D9',
                      borderRadius: '4px', fontSize: '0.875rem', backgroundColor: '#fff',
                    }}
                  >
                    {availableSheets.map((sheet) => (
                      <option key={sheet} value={sheet}>{sheet}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #DAD9D9', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                disabled={isProcessing}
              />
              Test-Modus (nur 1 ER- und 1 DR-Auftrag)
            </label>
            {testMode && (
              <span style={{
                padding: '0.2rem 0.5rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107',
                borderRadius: '3px', fontSize: '0.75rem', color: '#856404',
              }}>
                Nur 2 Auftraege werden verarbeitet
              </span>
            )}
          </div>

          {/* Modus: nur Notiz vs. Notiz + Status 5.67 (Nachlieferung Tor-Auftrag) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.6rem 0.75rem', backgroundColor: '#faf9f5', border: '1px solid #DAD9D9', borderRadius: '6px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Was soll bei DR-Auftraegen passieren?</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
              <input type="radio" name="glawel-status-mode" checked={!setStatusMode} onChange={() => setSetStatusMode(false)} disabled={isProcessing} />
              Nur KW-Notiz schreiben <span style={{ color: '#888' }}>(wie bisher, kein Status)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
              <input type="radio" name="glawel-status-mode" checked={setStatusMode} onChange={() => setSetStatusMode(true)} disabled={isProcessing} />
              KW-Notiz <strong>und</strong> Status <strong>5.67</strong> setzen (Nachlieferung Tor-Auftrag)
            </label>
            {setStatusMode && (
              <span style={{
                padding: '0.3rem 0.6rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107',
                borderRadius: '4px', fontSize: '0.78rem', color: '#856404',
              }}>
                ⚠️ Status 5.67 wird in Plenty geschrieben (echte Auftraege!) —
                {previewData ? <> markiert: <strong>{previewData.nachlieferungCount ?? 0}</strong> Kandidat(en) in dieser Datei.</> : ' Vorschau abwarten.'}
              </span>
            )}
          </div>

          <button
            onClick={handleImport}
            disabled={!selectedFile || !selectedSheet || isProcessing || availableFiles.length === 0 || (!!previewData && selectedRows.size === 0)}
            style={{
              width: '100%', padding: '0.625rem 1rem',
              backgroundColor: (isProcessing || (!!previewData && selectedRows.size === 0)) ? '#DAD9D9' : '#E9FF57',
              color: '#1A1A1A', border: 'none', borderRadius: '6px',
              fontWeight: 600, fontSize: '0.9rem', cursor: isProcessing ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            }}
          >
            <Upload size={16} style={isProcessing ? { animation: 'glawel-spin 1s linear infinite' } : undefined} />
            <span>
              {isProcessing
                ? (testMode ? 'Teste...' : 'Importiere...')
                : testMode
                  ? 'Test starten'
                  : !previewData
                    ? 'Import starten'
                    : selectedRows.size === 0
                      ? 'Nichts ausgewählt'
                      : allSelected
                        ? `Alle importieren (${allVisibleRowNumbers.length})`
                        : `Auswahl importieren (${selectedRows.size})`}
            </span>
          </button>
        </div>
      </div>

      {/* Fortschrittsanzeige */}
      {isRunning && progress && (
        <div style={{ background: '#fff', border: '1px solid #DAD9D9', borderRadius: '8px', marginBottom: '1rem' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DAD9D9', backgroundColor: '#E9FF57' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1A1A1A' }}>Import laeuft — Plenty wird aktualisiert</h3>
          </div>
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '0.5rem', textAlign: 'center', fontWeight: 600, color: '#1A1A1A' }}>
              Auftrag {progress.current} von {progress.total}
            </div>
            <div style={{ height: '10px', backgroundColor: '#DAD9D9', borderRadius: '5px', marginBottom: '0.75rem', overflow: 'hidden' }}>
              <div style={{
                height: '100%', backgroundColor: '#E9FF57', borderRadius: '5px',
                width: `${progress.percentage}%`, transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ padding: '0.75rem', backgroundColor: '#f0f4ff', border: '1px solid #c7d7f7', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Aktueller Status:</div>
              <div style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>{progress.message}</div>
              <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#888' }}>
                {progress.percentage}% abgeschlossen — Tab-Wechsel ist OK, der Job laeuft im Hintergrund weiter
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vorschau */}
      {selectedFile && previewData && !importResult && (
        <div style={{ background: '#fff', border: '1px solid #DAD9D9', borderRadius: '8px', marginBottom: '1rem' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DAD9D9', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Eye size={18} />
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Vorschau: {selectedFile}</h3>
          </div>
          <div style={{ padding: '1rem' }}>
            {isLoadingPreview ? (
              <div style={{ textAlign: 'center', color: '#666' }}>Lade Vorschau...</div>
            ) : (
              <>
                <div style={{ marginBottom: '0.75rem', color: '#666', fontSize: '0.85rem' }}>
                  Gesamt in Excel: <strong>{previewData.totalRows}</strong> verwertbare Zeilen — angezeigt wird nur was beim Lauf etwas tut.
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <div style={{
                    padding: '0.4rem 0.8rem', backgroundColor: '#e3f2fd',
                    borderLeft: '3px solid #2196F3', borderRadius: '4px', fontSize: '0.85rem',
                  }}>
                    <strong style={{ color: '#1976D2' }}>{previewData.outgoingNewCount}</strong> neue ER (Warenausgang)
                  </div>
                  <div style={{
                    padding: '0.4rem 0.8rem', backgroundColor: '#f3e5f5',
                    borderLeft: '3px solid #9c27b0', borderRadius: '4px', fontSize: '0.85rem',
                  }}>
                    <strong style={{ color: '#7b1fa2' }}>{previewData.noteCount}</strong> DR (Notizen)
                  </div>
                  <div style={{
                    padding: '0.4rem 0.8rem', backgroundColor: '#f5f5f5',
                    borderLeft: '3px solid #9e9e9e', borderRadius: '4px', fontSize: '0.85rem',
                  }}>
                    <strong style={{ color: '#616161' }}>{previewData.outgoingSkippedCount}</strong> ER bereits gebucht (uebersprungen)
                  </div>
                  <div style={{
                    padding: '0.4rem 0.8rem', backgroundColor: '#e8f5e9',
                    borderLeft: '3px solid #2e7d32', borderRadius: '4px', fontSize: '0.85rem',
                  }}>
                    <strong style={{ color: '#2e7d32' }}>{previewData.nachlieferungCount ?? 0}</strong> Nachlieferung → Status 5.67
                  </div>
                  {(() => {
                    // Terminvergleich AB ↔ geplante Liefer-KW: Kennzahlen aus der Vorschau
                    const warn = previewData.previewRows.filter((r) => r.abWarnung).length;
                    const ohneAb = previewData.previewRows.filter((r) => r.action === 'DR' && !r.abDatum).length;
                    return (
                      <>
                        <div style={{
                          padding: '0.4rem 0.8rem',
                          backgroundColor: warn > 0 ? '#ffebee' : '#f5f5f5',
                          borderLeft: `3px solid ${warn > 0 ? '#c62828' : '#bdbdbd'}`,
                          borderRadius: '4px', fontSize: '0.85rem',
                        }} title="Spanne zwischen Auftragsbestaetigung und Montag der geplanten Liefer-KW">
                          <strong style={{ color: warn > 0 ? '#c62828' : '#616161' }}>{warn}</strong> ueber 6 Wochen AB → Liefertermin
                        </div>
                        {warn > 0 && (
                          <button
                            onClick={handleExportPreviewWarnungen}
                            title="Diese Auftraege als Excel exportieren — reine Auswertung, es wird nichts in Plenty geschrieben"
                            style={{
                              padding: '0.4rem 0.8rem', backgroundColor: '#c62828', color: '#fff',
                              border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer',
                              fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
                            }}
                          >
                            <Download size={14} />
                            <span>{warn} exportieren</span>
                          </button>
                        )}
                        {ohneAb > 0 && (
                          <div style={{
                            padding: '0.4rem 0.8rem', backgroundColor: '#f5f5f5',
                            borderLeft: '3px solid #bdbdbd', borderRadius: '4px', fontSize: '0.85rem',
                          }} title="Fuer diese Auftraege liegt (noch) keine geparste Auftragsbestaetigung vor — das ist KEINE Entwarnung.">
                            <strong style={{ color: '#616161' }}>{ohneAb}</strong> ohne Auftragsbestaetigung
                          </div>
                        )}

                        {(previewData.fehlendeVorgaenge?.length ?? 0) > 0 && (() => {
                          const fehlend = previewData.fehlendeVorgaenge!;
                          const alt = fehlend.filter((f) => !f.istNeu);
                          return (
                            <details style={{
                              padding: '0.4rem 0.8rem',
                              backgroundColor: alt.length > 0 ? '#fff3e0' : '#f5f5f5',
                              borderLeft: `3px solid ${alt.length > 0 ? '#e65100' : '#bdbdbd'}`,
                              borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer',
                            }}>
                              <summary title="In Plenty offen bei diesem Lieferanten (Lieferauftraege bzw. Hauptauftraege ohne Lieferauftrag), aber NICHT in dieser Liste — der Lieferant kennt den Vorgang womoeglich nicht.">
                                <strong style={{ color: alt.length > 0 ? '#e65100' : '#616161' }}>{fehlend.length}</strong> in Plenty offen, fehlen in der Liste
                                {alt.length !== fehlend.length ? ` (${fehlend.length - alt.length} davon neu bestellt)` : ''}
                              </summary>
                              <div style={{ marginTop: '0.4rem', lineHeight: 1.7 }}>
                                {fehlend.map((f) => (
                                  <div key={f.auftragsnr}>
                                    <strong>{f.auftragsnr}</strong> — bestellt {f.bestelltAm ?? '?'}
                                    {f.istNeu && <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#616161', border: '1px solid #bdbdbd', borderRadius: '3px', padding: '0 0.3rem' }}>neu — vermutlich noch nicht in der Liste</span>}
                                  </div>
                                ))}
                              </div>
                            </details>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>

                <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem' }}>
                  <button
                    onClick={toggleAll}
                    style={{
                      padding: '0.25rem 0.7rem', border: '1px solid #DAD9D9', borderRadius: '4px',
                      background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                    }}
                  >
                    {allSelected ? 'Alle abwählen' : 'Alle auswählen'}
                  </button>
                  <span style={{ color: '#555' }}>
                    <strong>{selectedRows.size}</strong> von {allVisibleRowNumbers.length} ausgewählt — es werden nur die angehakten Aufträge importiert.
                  </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'center', border: '1px solid #dee2e6', fontWeight: 600, width: '36px' }}>
                          <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Alle aus-/abwählen" />
                        </th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', border: '1px solid #dee2e6', fontWeight: 600 }}>Zeile</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center', border: '1px solid #dee2e6', fontWeight: 600 }}>Aktion</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', border: '1px solid #dee2e6', fontWeight: 600 }}>Auftragsnr.</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', border: '1px solid #dee2e6', fontWeight: 600 }}>KW / Liefertermin</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', border: '1px solid #dee2e6', fontWeight: 600 }}>AB / Differenz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {erRows.map((row) => (
                        <tr key={`er-${row.rowNumber}`} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: selectedRows.has(row.rowNumber) ? 'rgba(33,150,243,0.04)' : '#fafafa', opacity: selectedRows.has(row.rowNumber) ? 1 : 0.55 }}>
                          <td style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #f0f0f0' }}>
                            <input type="checkbox" checked={selectedRows.has(row.rowNumber)} onChange={() => toggleRow(row.rowNumber)} />
                          </td>
                          <td style={{ padding: '0.4rem', color: '#888', fontFamily: 'monospace', fontSize: '0.75rem', border: '1px solid #f0f0f0' }}>{row.rowNumber}</td>
                          <td style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #f0f0f0' }}>
                            <span style={{ padding: '0.15rem 0.5rem', backgroundColor: '#1976D2', color: '#fff', borderRadius: '3px', fontWeight: 700, fontSize: '0.75rem' }}>ER</span>
                          </td>
                          <td style={{ padding: '0.4rem', fontFamily: 'monospace', border: '1px solid #f0f0f0' }}>{row.orderId}</td>
                          <td style={{ padding: '0.4rem', color: '#9e9e9e', fontSize: '0.75rem', border: '1px solid #f0f0f0' }}>—</td>
                        </tr>
                      ))}
                      {drRows.map((row) => (
                        <tr key={`dr-${row.rowNumber}`} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: selectedRows.has(row.rowNumber) ? 'rgba(156,39,176,0.04)' : '#fafafa', opacity: selectedRows.has(row.rowNumber) ? 1 : 0.55 }}>
                          <td style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #f0f0f0' }}>
                            <input type="checkbox" checked={selectedRows.has(row.rowNumber)} onChange={() => toggleRow(row.rowNumber)} />
                          </td>
                          <td style={{ padding: '0.4rem', color: '#888', fontFamily: 'monospace', fontSize: '0.75rem', border: '1px solid #f0f0f0' }}>{row.rowNumber}</td>
                          <td style={{ padding: '0.4rem', textAlign: 'center', border: '1px solid #f0f0f0' }}>
                            <span style={{ padding: '0.15rem 0.5rem', backgroundColor: '#7b1fa2', color: '#fff', borderRadius: '3px', fontWeight: 700, fontSize: '0.75rem' }}>DR</span>
                          </td>
                          <td style={{ padding: '0.4rem', fontFamily: 'monospace', border: '1px solid #f0f0f0' }}>{row.orderId}</td>
                          <td style={{ padding: '0.4rem', border: '1px solid #f0f0f0' }}>
                            <span style={{
                              padding: '0.15rem 0.5rem',
                              backgroundColor: row.deliveryTime === 'unverplant' ? '#ff9800' : '#9c27b0',
                              color: '#fff', borderRadius: '3px', fontSize: '0.75rem',
                            }}>
                              {row.deliveryTime}
                            </span>
                            {row.openPositions && row.openPositions.length > 0 && (
                              <div style={{ marginTop: '0.3rem', fontSize: '0.72rem', color: '#555' }}>
                                <div style={{ fontWeight: 600, color: '#7b1fa2' }}>Offene Positionen:</div>
                                <ul style={{ margin: '0.15rem 0 0', paddingLeft: '1rem' }}>
                                  {row.openPositions.map((p, i) => (
                                    <li key={i}>{p}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {row.isNachlieferung && (
                              <div style={{ marginTop: '0.3rem' }} title={row.nachlieferungReason}>
                                <span style={{
                                  padding: '0.1rem 0.45rem', backgroundColor: '#2e7d32', color: '#fff',
                                  borderRadius: '3px', fontWeight: 700, fontSize: '0.7rem',
                                }}>
                                  → Status 5.67
                                </span>
                              </div>
                            )}
                            {row.mailPreview && (
                              <div style={{ marginTop: '0.3rem' }}>
                                <span style={{
                                  padding: '0.1rem 0.45rem',
                                  backgroundColor: row.mailPreview.startsWith('✉️') ? '#1565c0' : row.mailPreview.includes('fällig') ? '#ef6c00' : '#757575',
                                  color: '#fff', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600,
                                }}>
                                  {row.mailPreview}
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #dee2e6', verticalAlign: 'top' }}>
                            <AbVergleichZelle row={row} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {erRows.length + drRows.length === 0 && (
                  <div style={{ marginTop: '0.75rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center', color: '#666' }}>
                    Nichts zu tun — alle ER-Auftraege wurden bereits gebucht und es gibt keine DR-Auftraege.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Importergebnis (live waehrend Lauf + final) */}
      {importResult && (
        <div style={{ background: '#fff', border: '1px solid #DAD9D9', borderRadius: '8px', marginBottom: '1rem' }}>
          <div style={{
            padding: '0.75rem 1rem', borderBottom: '1px solid #DAD9D9',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              {isRunning ? 'Verarbeitung laeuft...' : 'Import-Ergebnis'}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {!isRunning && (importResult.abWarnCount ?? 0) > 0 && (
              <button
                onClick={() => handleExportResults(true)}
                title="Nur die Auftraege exportieren, bei denen zwischen Auftragsbestaetigung und geplantem Liefertermin mehr als 6 Wochen liegen"
                style={{
                  padding: '0.375rem 0.75rem', backgroundColor: '#c62828', color: '#fff',
                  border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <AlertCircle size={14} />
                <span>{importResult.abWarnCount} Warnungen exportieren</span>
              </button>
            )}
            {!isRunning && importResult.detailedResults?.length > 0 && (
              <button
                onClick={() => handleExportResults(false)}
                style={{
                  padding: '0.375rem 0.75rem', backgroundColor: '#E9FF57', color: '#1A1A1A',
                  border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <Download size={14} />
                <span>Excel exportieren</span>
              </button>
            )}
            </div>
          </div>

          <div style={{ padding: '1rem' }}>
            {importResult.sheetName && (
              <div style={{
                marginBottom: '0.75rem', padding: '0.5rem 0.75rem',
                backgroundColor: '#e8f4fd', borderRadius: '4px', fontSize: '0.875rem',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}>
                <FileSpreadsheet size={14} />
                <span><strong>Verwendeter Reiter:</strong> {importResult.sheetName}</span>
              </div>
            )}

            {/* Nachlieferung-Status-Info */}
            {((importResult.nachlieferungCandidates ?? 0) > 0 || importResult.statusMode === 'note-and-status') && (
              <div style={{
                marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem',
                backgroundColor: importResult.statusCapHit ? '#fff3cd' : '#e8f5e9',
                border: `1px solid ${importResult.statusCapHit ? '#ffc107' : '#a5d6a7'}`,
              }}>
                <strong>Nachlieferung (5.67):</strong>{' '}
                {importResult.statusMode === 'note-and-status'
                  ? <>{importResult.statusSetCount ?? 0} von {importResult.nachlieferungCandidates ?? 0} Kandidaten auf Status 5.67 gesetzt.</>
                  : <>{importResult.nachlieferungCandidates ?? 0} Kandidat(en) erkannt — Modus „nur Notiz", kein Status gesetzt.</>}
                {importResult.statusCapHit && <> ⚠️ Sanity-Cap ausgeloest — Status wurde NICHT geschrieben, bitte Datenlage pruefen.</>}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
              <StatCard label="Gesamt" value={importResult.totalRows} color="#1A1A1A" />
              <StatCard label="Verarbeitet" value={importResult.processedOrders} color="#1A1A1A" />
              <StatCard label="Erfolgreich" value={importResult.successCount} color="#2e7d32" icon={<CheckCircle size={14} />} />
              <StatCard label="Fehler" value={importResult.errorCount} color={importResult.errorCount > 0 ? '#c62828' : '#1A1A1A'} icon={<XCircle size={14} />} />
              <StatCard label="Schon gebucht" value={importResult.skippedCount ?? 0} color="#616161" />
              <StatCard label="Storniert" value={importResult.cancelledCount ?? 0} color={(importResult.cancelledCount ?? 0) > 0 ? '#ef6c00' : '#1A1A1A'} icon={<AlertCircle size={14} />} />
            </div>

            {importResult.detailedResults?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                  Verarbeitete Auftraege ({importResult.detailedResults.length})
                </h4>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                      <tr>
                        <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 600, width: '40px' }}>#</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 600, width: '70px' }}>Aktion</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Auftrag</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Status</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600, width: '150px' }}>Vorher → Nachher</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600, width: '130px' }}>AB / Differenz</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 600 }}>Plenty Antwort</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.detailedResults.map((r, idx) => (
                        <tr key={`${r.actionType}-${r.orderId}-${idx}`} style={{
                          borderBottom: '1px solid #dee2e6',
                          backgroundColor: r.skipped
                            ? 'rgba(239,108,0,0.08)'
                            : (r.success ? 'rgba(76,175,80,0.07)' : 'rgba(244,67,54,0.07)'),
                        }}>
                          <td style={{ padding: '0.4rem', textAlign: 'center', color: '#888', fontSize: '0.75rem' }}>{idx + 1}</td>
                          <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.15rem 0.5rem',
                              backgroundColor: r.actionType === 'ER' ? '#1976D2' : '#7b1fa2',
                              color: '#fff', borderRadius: '3px', fontWeight: 700, fontSize: '0.7rem',
                            }}>
                              {r.actionType}
                            </span>
                          </td>
                          <td style={{ padding: '0.4rem' }}>
                            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                              {r.orderId}
                              {r.statusSet && (
                                <span style={{ padding: '0.05rem 0.4rem', backgroundColor: '#2e7d32', color: '#fff', borderRadius: '3px', fontWeight: 700, fontSize: '0.65rem' }}>
                                  5.67 ✓
                                </span>
                              )}
                              {r.isNachlieferung && !r.statusSet && (
                                <span style={{ padding: '0.05rem 0.4rem', backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32', borderRadius: '3px', fontWeight: 700, fontSize: '0.65rem' }} title={r.statusReason || r.nachlieferungReason}>
                                  Nachlief.
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                              PM-ID: {r.plentyOrderId || '-'}
                              {r.deliveryTime && r.actionType === 'DR' && <> | KW: {r.deliveryTime}</>}
                              {r.finalStatus !== undefined && <> | Status: {r.finalStatus}</>}
                              {r.skipped && r.statusBefore !== undefined && <> | Status: {r.statusBefore}</>}
                            </div>
                          </td>
                          <td style={{ padding: '0.4rem', textAlign: 'center' }} title={r.skipped ? r.skipReason : undefined}>
                            {r.skipped ? (
                              <AlertCircle size={20} style={{ color: '#ef6c00' }} />
                            ) : r.success ? (
                              <CheckCircle size={20} style={{ color: '#4CAF50' }} />
                            ) : (
                              <XCircle size={20} style={{ color: '#f44336' }} />
                            )}
                          </td>
                          <td style={{ padding: '0.4rem', fontSize: '0.75rem' }}>
                            {r.actionType === 'ER' ? (
                              <span style={{ fontFamily: 'monospace' }}>
                                {r.statusBefore !== undefined ? <span style={{ color: '#888' }}>{r.statusBefore}</span> : '-'}
                                <span style={{ color: '#bbb', margin: '0 0.3rem' }}>→</span>
                                {r.skipped ? (
                                  <strong style={{ color: '#ef6c00' }}>storniert</strong>
                                ) : r.finalStatus !== undefined ? (
                                  <strong style={{ color: Math.floor(r.finalStatus) === 7 ? '#2e7d32' : '#c62828' }}>{r.finalStatus}</strong>
                                ) : '-'}
                              </span>
                            ) : (
                              <span style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3rem' }}>
                                <span style={{ color: '#888' }}>{r.noteBefore || '-'}</span>
                                <span style={{ color: '#bbb' }}>→</span>
                                <strong style={{ color: r.noteCreated ? '#2e7d32' : '#c62828', whiteSpace: 'pre-line' }}>{r.noteAfter || r.deliveryTime || '-'}</strong>
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.4rem', verticalAlign: 'top' }}>
                            <AbVergleichZelle row={r} />
                          </td>
                          <td style={{ padding: '0.4rem', fontSize: '0.75rem', color: r.success ? '#444' : '#c62828' }}>
                            {r.error ? <strong>{r.error}</strong> : (r.plentyResponse || '-')}
                          </td>
                        </tr>
                      ))}
                      {isRunning && progress && progress.current > importResult.detailedResults.length && (
                        <tr>
                          <td colSpan={7} style={{
                            padding: '0.5rem',
                            textAlign: 'center',
                            color: '#1976D2',
                            fontStyle: 'italic',
                            backgroundColor: '#e3f2fd',
                          }}>
                            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.3rem', animation: 'glawel-spin 1.2s linear infinite' }} />
                            verarbeite naechsten Auftrag...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isRunning && importResult.errors?.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem', fontWeight: 600, color: '#c62828' }}>
                  Fehler-Details ({importResult.errors.length}):
                </h4>
                <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ backgroundColor: '#fff3f3', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #f5c6cb', fontWeight: 600 }}>Auftragsnr.</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #f5c6cb', fontWeight: 600 }}>Fehler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.errors.map((err, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f5c6cb' }}>
                          <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{err.orderId}</td>
                          <td style={{ padding: '0.5rem', color: '#c62828' }}>{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      </>
      )}

      <style>{`
        @keyframes glawel-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ─── Sub-Komponenten ──────────────────────────────────────────────────────────

function StatCard({
  label, value, color, icon,
}: { label: string; value: number | string; color: string; icon?: React.ReactNode }) {
  return (
    <div style={{ padding: '0.6rem', border: '1px solid #DAD9D9', borderRadius: '6px', textAlign: 'center', backgroundColor: '#fff' }}>
      <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.2rem' }}>
        {icon}
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
